import { useEffect, useMemo, useState } from "react";
import { Button, Form, Input, Select } from "antd";
import Swal from "sweetalert2";
import jalaliday from "jalaliday";
import moment from "jalali-moment";
import dayjs, { Dayjs } from "dayjs";
import { Option } from "antd/es/mentions";
import { useQuery } from "@tanstack/react-query";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import {
  FormQuestion,
  bareQuestions,
  farmQuestions,
  orchardQuestions,
} from "@/features/growing-season/utils/form-questions";
import Selection from "@/shared/components/selection/presentation/Selection";
import CalendarJalali from "@/shared/components/calendarJalali/presentation/CalendarJalali";
import {
  useMinMaxAutumn,
  useMinMaxGrowingSeason,
  useMinMaxSpring,
} from "@/shared/components/calendarJalali/utils/useMinMaxGrowingSeason";
import { useMinPlantingDate } from "@/shared/components/calendarJalali/utils/useMinPlantingDate";
import {
  disableCalendarDate,
  isSpring,
} from "@/shared/components/calendarJalali/utils/calendarDateDisabler";
import { useMinStartBarePeriodDate } from "@/shared/components/calendarJalali/utils/useMinStartBarePeriodDate";
import {
  submitDateChecker,
  submitPolygon,
} from "@/shared/components/calendarJalali/utils/submitDateChecker";
import AgroMapSeason from "@/features/map/presentation/components/AgroMapSeason";
import { FieldMarkerIcon } from "@/components/map/utils/customs";
import { toolbarBaseControls } from "@/features/map/utils/configs";
import {
  Product,
  Season,
} from "@/features/growing-season/domain/growing-season.interface";
import { Farm } from "@/features/farm/domain/farm.interface";
import {
  GrowingSeasonRepository,
  GrowingSeasonRepositoryImpl,
} from "@/features/growing-season/data/repositories/growing-season-repositories";
import ReactQueryAsyncValueWrapper from "@/shared/components/wrappers/ReactQueryAsyncValueWrapper";

dayjs.extend(jalaliday);

export type GrowingSeasonType = "add" | "edit" | "addModal";

// text area for "توضیحات"
const { TextArea } = Input;

interface GrowingSeasonProps {
  type: GrowingSeasonType;
  season?: Season | null;
  farm: Farm;
  loadingSubmitForm: boolean;
  cancel: () => void;
  onSubmitForm: (season: Partial<Season>) => void;
}

const GrowingSeason = ({
  type,
  season,
  farm,
  loadingSubmitForm,
  cancel,
  onSubmitForm,
}: GrowingSeasonProps) => {
  // consts
  const growingSeasonRepository: GrowingSeasonRepository = useMemo(() => {
    return new GrowingSeasonRepositoryImpl();
  }, []);

  // @ts-ignore
  dayjs.calendar("jalali");

  const fromInitialValue = {
    growingSeason:
      (season?.growingSeason &&
        dayjs(`${season?.growingSeason}-04-01`).format("YYYY")) ??
      null,
    productId: season?.productId ?? null,
    cultivarName: season?.cultivarName ?? null,
    plantingDate: season?.plantingDate ?? null,
    harvestDate: season?.harvestDate ?? null,
    irrigationSystemId: season?.irrigationSystemId ?? null,
    usedSeedAmount: season?.usedSeedAmount ?? null,
    treesAge: season?.treesAge ?? null,
    density: season?.density ?? null,
    description: season?.description ?? null,
    startBarePeriodDate: season?.startBarePeriodDate ?? null,
    endBarePeriodDate: season?.endBarePeriodDate ?? null,
  };
  // states
  const [form] = Form.useForm();
  const [formType, setFormType] = useState<"FARM" | "ORCHARD" | "BARE">("FARM");
  const [questionList, setQuestionList] = useState<FormQuestion[]>([]);
  const [densityUnit, setDensityUnit] = useState<string>("1");
  const [showPolygonDrawing, setShowPolygonDrawing] = useState<boolean>(false);
  // const [updatedPolygonData, setUpdatedPolygonData] =
  //   useState<Partial<Season>>();
  const [polygonData, setPolygonData] = useState<Partial<Season>>();
  const [hasPolygon, setHasPolygon] = useState(false);
  const [selectedGrowingSeason, setSelectedGrowingSeason] = useState<number>(
    type === "edit" ? season?.growingSeason! : new Date().getFullYear()
  );
  const [product, setProduct] = useState<Partial<Product> | undefined>(
    undefined
  );
  const [disabledHarvest, setDisabledHarvest] = useState<boolean>(true);
  const [plantingDate, setPlantingDate] = useState<string>();
  const [startBarePeriodDate, setStartBarePeriodDate] = useState<string>();
  const [minGrowingSeason, maxGrowingSeason] = useMinMaxGrowingSeason(
    selectedGrowingSeason
  );
  const [minSpring, maxSpring] = useMinMaxSpring(selectedGrowingSeason);
  const [minAutumn, maxAutumn] = useMinMaxAutumn(selectedGrowingSeason);

  const [minPlantingDate] = useMinPlantingDate(
    form.getFieldValue("plantingDate")
  );
  const [minStartBarePeriodDate] = useMinStartBarePeriodDate(
    form.getFieldValue("startBarePeriodDate")
  );

  //useQuery
  const farmSeasonsQuery = useQuery(
    [
      "getAllGrowingSeasons",
      farm?.id,
      form.getFieldValue("plantingDate"),
      form.getFieldValue("growingSeason"),
    ],
    () =>
      growingSeasonRepository._getAllGrowingSeasons({
        farmId: farm?.id,
        validGrowingSeasons: {
          future: false,
          present: true,
          past: false,
          isBare: false,
          date:
            formType === "FARM"
              ? form.getFieldValue("plantingDate")
              : formType === "ORCHARD"
              ? moment(form.getFieldValue("growingSeason"), "jYYYY")
                  .locale("en")
                  .format("YYYY") + "-04-01"
              : form.getFieldValue("startBarePeriodDate"),
        },
      }),
    {
      enabled:
        // افزودن فصل رشدی در ثبت زمین که ایدی زمین نداریم نیاز به گرفتن فصل رشدی ها نیست
        !!farm?.id &&
        !!form.getFieldValue("growingSeason") &&
        !!form.getFieldValue("plantingDate"),
    }
  );

  // functions
  function disabledDate(everyDay: Dayjs, dateType: string): boolean {
    // این شرط همیشه اول بررسی شود چون محدوده کوچکتری را چک میکند
    if (type === "edit" && formType === "FARM" && dateType === "plantingDate") {
      if (isSpring(dayjs(season?.plantingDate), minSpring, maxSpring)) {
        return disableCalendarDate(everyDay, minSpring, maxSpring);
      } else {
        return disableCalendarDate(everyDay, minAutumn, maxAutumn);
      }
    }

    if (
      type === "edit" &&
      formType === "BARE" &&
      dateType === "startBarePeriodDate"
    ) {
      if (isSpring(dayjs(season?.startBarePeriodDate), minSpring, maxSpring)) {
        return disableCalendarDate(everyDay, minSpring, maxSpring);
      } else {
        return disableCalendarDate(everyDay, minAutumn, maxAutumn);
      }
    }

    if (formType === "FARM" && dateType === "plantingDate") {
      return disableCalendarDate(everyDay, minGrowingSeason, maxGrowingSeason);
    }

    if (formType === "FARM" && dateType === "harvestDate") {
      return disableCalendarDate(everyDay, minPlantingDate);
    }

    if (formType === "ORCHARD" && dateType === "plantingDate") {
      return disableCalendarDate(everyDay, undefined, maxGrowingSeason);
    }

    if (formType === "ORCHARD" && dateType === "harvestDate") {
      return disableCalendarDate(everyDay, minGrowingSeason);
    }

    if (formType === "BARE" && dateType === "startBarePeriodDate") {
      return disableCalendarDate(everyDay, minGrowingSeason, maxGrowingSeason);
    }

    if (formType === "BARE" && dateType === "endBarePeriodDate") {
      return disableCalendarDate(everyDay, minStartBarePeriodDate);
    }

    return false;
  }

  function handleMap() {
    if (
      form.getFieldValue("growingSeason") &&
      form.getFieldValue("plantingDate")
    ) {
      setShowPolygonDrawing((prev) => !prev);
    } else {
      Swal.fire({
        icon: "warning",
        title: "توجه",
        text: "جهت ثبت مختصات زمین باید ابتدا فصل رشد(سال) و تاریخ کاشت تعیین شده باشد",
        cancelButtonText: "متوجه شدم",
        showCancelButton: true,
        showConfirmButton: false,
      });
    }
  }

  function submit(values: any) {
    const validation = submitDateChecker(
      values,
      minGrowingSeason,
      maxGrowingSeason,
      minPlantingDate,
      minStartBarePeriodDate,
      formType
    );

    const validationPolygon = submitPolygon(polygonData, type);

    if (!validation || !validationPolygon) {
      return;
    }

    const growingSeasonData: Partial<Season> = {
      ...values,
      growingSeason: moment(values?.growingSeason, "jYYYY")
        .locale("en")
        .format("YYYY"),
      isBare: values.productId === 0 ? true : false,
    };

    // زمانی که کامپوننت فصل رشدی در مدال باز میشود نقشه وجود ندارد
    // پس اطلاعات آپدیت شده پلیگان هم وجود نخواهد داشت
    // در غیر اینصورت نقشه وجود دارد
    // polygonData -> هم در حالت افزودن فصل رشدی و هم ویرایش آن این متغیر اطلاعات پلیگان را در خود ذخیره می کند
    if (type !== "addModal") {
      if (polygonData?.polygon && polygonData?.polygon?.length > 0) {
        growingSeasonData["polygon"] = polygonData["polygon"];
        growingSeasonData["centerCoordinates"] =
          polygonData["centerCoordinates"];
        growingSeasonData["area"] = polygonData["area"];
      }
    } else {
      // در سرویس افزودن زمین امکان ثبت فصل رشدی هم وجود دارد که در آن قسمت پس از ثبت فصل رشدی
      // باید اطلاعات کشت انتخابی هم به کاربر نمایش داده شود به همین خاطر کل آبجکت پروداکت هم به آبجکت اضافه می شود اما به سرور ارسال نمیشود (نیازی نیست)
      //  @ts-ignore
      growingSeasonData["product"] = product;
    }

    if (densityUnit === "2" && growingSeasonData["density"]) {
      growingSeasonData["density"] = growingSeasonData["density"] * 10000;
    }

    if (values?.harvestDate && season?.harvestDate === values["harvestDate"]) {
      growingSeasonData["harvestDate"] = moment(values["harvestDate"])
        .locale("en")
        .format("YYYY-MM-DD");
    }

    if (
      values?.plantingDate &&
      season?.plantingDate === values["plantingDate"]
    ) {
      growingSeasonData["plantingDate"] = moment(values["plantingDate"])
        .locale("en")
        .format("YYYY-MM-DD");
    }

    onSubmitForm(growingSeasonData);
  }

  // lifecycle
  useEffect(() => {
    if (type === "add" || type === "addModal") {
      setFormType("FARM");
    } else if (type === "edit") {
      setPolygonData({
        polygon: season!.polygon,
        centerCoordinates: season!.centerCoordinates,
        area: season!.area,
      });
      if (season!.product) {
        if (season!.product.type === "FARM") {
          setFormType("FARM");
        } else {
          setFormType("ORCHARD");
        }
      } else {
        setFormType("BARE");
      }
    }
    if (type === "addModal" && season?.product) {
      setFormType(season?.product?.type ?? "FARM");
      setProduct(season?.product);
      setDisabledHarvest(false);
    }
    if (season?.plantingDate) {
      setDisabledHarvest(false);
    }
  }, []);

  useEffect(() => {
    if (formType === "FARM") {
      setQuestionList(farmQuestions);
    }
    if (formType === "ORCHARD") {
      setQuestionList(orchardQuestions);
    }
    if (formType === "BARE") {
      setQuestionList(bareQuestions);
    }
  }, [formType]);

  return (
    <div className="flex flex-col items-start justify-center w-full">
      <div className="pl-16 bg-[#B3DACC] border-2 border-[#DDE6EF] font-bold rounded-t-lg border-b-0 pr-3 py-1">
        {type === "edit" ? "ویرایش اطلاعات" : "افزودن فصل رشدی"}
      </div>

      <section className="flex agrobg rounded-xl rounded-tr-none flex-row w-full border-2">
        {showPolygonDrawing ? (
          <ReactQueryAsyncValueWrapper state={farmSeasonsQuery}>
            {(farmSeasons) => {
              return (
                <div className="flex w-full h-[580px] max-lg:absolute max-lg:top-0 max-lg:left-0 max-lg:w-[100vw] max-lg:h-[95vh] p-2 z-[9999]">
                  <AgroMapSeason
                    type={type === "add" ? "add" : "edit"}
                    mapId="20059898"
                    markerIcon={FieldMarkerIcon}
                    // @ts-ignore
                    toolbarOptions={
                      type === "add"
                        ? {
                            ...toolbarBaseControls,
                            drawPolyline: true,
                          }
                        : toolbarBaseControls
                    }
                    parentFarmPolygon={farm.polygon}
                    // در حالت ویرایش فصل رشدی خود فصل رشدی مدنظر برای ویرایش باید از لیست فصل رشدی های حال حاضری که
                    // صرفا برای نمایش به کامپوننت پاس داده میشوند باید حذف شود
                    farmSeasons={
                      type === "add"
                        ? farmSeasons
                        : farmSeasons!.filter(
                            (farmSeason) => farmSeason.id !== season?.id
                          )
                    }
                    currentSeasonPolygon={polygonData?.polygon}
                    productId={
                      fromInitialValue?.productId !== null
                        ? fromInitialValue?.productId
                        : undefined
                    }
                    onConfirm={(polygonData) => {
                      if (polygonData?.polygon?.length > 0) {
                        setHasPolygon(true);
                        setPolygonData({
                          polygon: polygonData.polygon,
                          centerCoordinates: polygonData.centerCoordinates,
                          area: polygonData.area,
                        });
                      }
                      setShowPolygonDrawing(false);
                    }}
                    onCancel={() => setShowPolygonDrawing((prev) => !prev)}
                  />
                </div>
              );
            }}
          </ReactQueryAsyncValueWrapper>
        ) : (
          <Form
            form={form}
            autoComplete="off"
            className="flex flex-col items-center justify-center"
            layout="vertical"
            onFinish={submit}
            initialValues={fromInitialValue}
          >
            <div className="w-full flex flex-row max-lg:flex-col-reverse items-center justify-center p-2 gap-3 bg-red400">
              <div
                className={`${
                  type !== "addModal" ? "w-[60%] max-lg:w-full" : "w-[780px]"
                } `}
              >
                <div
                  className={`flex lg:flex-wrap max-lg:flex-col max-lg:p-2 gap-y-0 ${
                    type !== "addModal"
                      ? "justify-between"
                      : "justify-start gap-2"
                  } max-lg:justify-center max-lg:items-center`}
                >
                  {questionList?.map((item, i) => {
                    return (
                      <div
                        key={item.name}
                        className="flex flex-col max-lg:justify-center items-start lg:-my-1"
                      >
                        <Form.Item
                          key={i}
                          required={item.require}
                          name={item?.name}
                          rules={item?.rule}
                          label={item.title}
                        >
                          {item?.name === "description" ? (
                            <TextArea
                              autoComplete="nope"
                              placeholder={item?.placeholder}
                              className="round w-[240px] !rounded-2xl max-lg:p-1 max-lg:text-[12px] input-neo outline-none"
                              autoSize={{
                                minRows: 1,
                                maxRows: 2,
                              }}
                              onChange={(e) =>
                                form.setFieldValue(item?.name, e?.target?.value)
                              }
                            />
                          ) : item?.kind === "inputText" ||
                            item?.kind === "inputNumber" ? (
                            <Input
                              type={
                                item?.kind === "inputText"
                                  ? "textarea"
                                  : "number"
                              }
                              dir="rtl"
                              className={`w-[240px] rounded-3xl max-lg:p-1 max-lg:text-[12px] input-neo outline-none justify-end items-center gap-2.5 flex`}
                              placeholder={item?.placeholder}
                              onChange={(e) =>
                                form.setFieldValue(item?.name, e?.target?.value)
                              }
                              addonAfter={
                                item.name === "density" ? (
                                  <Select
                                    className="w-[100px] !border-none"
                                    defaultValue={"1"}
                                    onChange={(value) => {
                                      setDensityUnit(value);
                                    }}
                                  >
                                    <Option value={"1"}>در هکتار</Option>
                                    <Option value={"2"}>در متر مربع</Option>
                                  </Select>
                                ) : null
                              }
                            />
                          ) : item?.kind === "select" ? (
                            <Selection
                              key={item.name}
                              index={item.name}
                              type={item.name}
                              defaultValue={form.getFieldValue(item.name)}
                              disabled={
                                type === "edit" &&
                                (item.name === "growingSeason" ||
                                  item.name === "productId")
                              }
                              onChange={(value, option) => {
                                form.setFieldValue(item?.name, value);
                                if (item?.name === "productId") {
                                  setProduct({
                                    id: option.value,
                                    persianName: option.label,
                                    type: option.type,
                                  });
                                  setFormType(option.type);
                                }
                                if (item?.name === "growingSeason") {
                                  setSelectedGrowingSeason(
                                    +moment
                                      .from(value, "fa", "YYYY")
                                      .format("YYYY")
                                  );
                                  if (type === "add") {
                                    setHasPolygon(false);
                                    setPolygonData(undefined);
                                  }
                                }
                              }}
                            />
                          ) : item?.kind === "datePicker" ? (
                            <CalendarJalali
                              key={item?.name}
                              disabled={
                                item?.name === "harvestDate" && disabledHarvest
                              }
                              disabledDate={(everyDay) =>
                                disabledDate(everyDay, item.name)
                              }
                              //@ts-ignore
                              // initialValue={fromInitialValue[`${item.name}`]}
                              initialValue={form.getFieldValue(item.name)}
                              onChange={(objectEvent) => {
                                // @ts-ignore
                                const date = moment(objectEvent?.$d).format(
                                  "YYYY-MM-DD"
                                );

                                form.setFieldValue(item?.name, date);
                                if (item?.name === "plantingDate") {
                                  setPlantingDate(date);
                                  if (type === "add") {
                                    setHasPolygon(false);
                                    setPolygonData(undefined);
                                  }
                                }
                                if (item?.name === "startBarePeriodDate") {
                                  setStartBarePeriodDate(date);
                                  if (type === "add") {
                                    setHasPolygon(false);
                                    setPolygonData(undefined);
                                  }
                                }
                                setDisabledHarvest(false);
                              }}
                            />
                          ) : (
                            ""
                          )}
                        </Form.Item>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* map  */}
              {type !== "addModal" && (
                <div className="flex flex-col cardbg rounded-xl overflow-clip gap-4 w-[40%] max-lg:w-[280px]">
                  {/* map container */}
                  <div className="relative cursor-pointer h-[380px] max-lg:h-[280px]">
                    <div
                      className={`absolute w-full h-full max-lg:h-[270px] top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 ${
                        !polygonData?.polygon ? "blur-[2px]" : ""
                      } pointer-events-none`}
                    >
                      <AgroMapSeason
                        type="onlyShow"
                        mapId="2005989845245"
                        markerIcon={FieldMarkerIcon}
                        // @ts-ignore
                        toolbarOptions={toolbarBaseControls}
                        parentFarmPolygon={farm.polygon}
                        currentSeasonPolygon={polygonData?.polygon}
                      />
                    </div>

                    <div
                      onClick={handleMap}
                      className="absolute flex flex-col w-full items-center justify-center gap-2 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
                    >
                      <div className="flex border-8 rounded-2xl w-40 h-40 border-white items-center justify-center text-[7rem] text-white !font-light">
                        {!hasPolygon ? <PlusOutlined /> : <EditOutlined />}
                      </div>

                      <div className="!text-center whitespace-nowrap border-white text-[24px] text-white font-extrabold ">
                        {!hasPolygon
                          ? "تعیین محدوده فصل رشد"
                          : " ویرایش محدوده فصل رشد"}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* action buttons */}
            <div className="flex p-1 items-center justify-center gap-6">
              <Form.Item htmlFor="submit">
                <Button
                  loading={loadingSubmitForm}
                  htmlType="submit"
                  className="button-green !text-white hover:brightness-110 h-fit py-1.5 max-lg:px-5 px-16"
                >
                  ثبت تغییرات
                </Button>
              </Form.Item>

              <Form.Item>
                <Button
                  onClick={cancel}
                  className="bg-red-600 !text-white rounded-2xl hover:brightness-110 h-fit py-1.5 max-lg:px-12 px-14"
                >
                  لغو
                </Button>
              </Form.Item>
            </div>
          </Form>
        )}
      </section>
    </div>
  );
};

export default GrowingSeason;
